import { useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { Button, ListGroup, InputGroup, FormControl, Card } from "react-bootstrap";
import { BsEmojiSmile, BsPaperclip } from "react-icons/bs";
import WrapperComponent from "../commonComponents/WrapperComponent";
import "./ChatPage.css";
import { url } from "../../util/config";

const EMOJI_GROUPS = {
  "😀 Smileys": ["😀", "😊", "😅", "😢", "😍", "🤔", "😎", "🥳"],
  "👍 Gestures": ["👍", "👏", "🙌", "✋", "🤝", "👋", "🎉", "💪"],
  "❤️ Hearts": ["❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍"],
  "🎯 Action": ["🎯", "⚡", "🔥", "💡", "✨", "🚀", "📱", "💻"],
};

export default function ChatPage() {
  const [unassigned, setUnassigned] = useState([]);
  const [active, setActive] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [message, setMessage] = useState("");  
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const uid = localStorage.getItem("uid");
  const socketRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch chats from API
  const fetchChats = async () => {
    try {
      const unassignedRes = await fetch(`${url}/unassigned/${uid}`);
      const unassignedData = await unassignedRes.json();
      setUnassigned(unassignedData.unassignedChats || []);

      const activeRes = await fetch(`${url}/active/${uid}`);
      const activeData = await activeRes.json();
      setActive(activeData.activeChats || []);

      setSelectedChat((prev) => {
      if (!prev) return prev;
      const updated = [...(unassignedData.unassignedChats || []), ...(activeData.activeChats || [])]
        .find((c) => c._id === prev._id);
      return updated ? updated : prev;
    });

    } catch (err) {
      console.error("Error fetching chats:", err);
    }
  };

  useEffect(() => {
    if (!uid) return;

    socketRef.current = io("http://localhost:3000");
    socketRef.current.emit("registerAgent", uid);

    socketRef.current.on("message:new", (conversation) => {
      // Update selectedChat immediately if it's open
      setSelectedChat((prev) =>
        prev?._id === conversation._id ? conversation : prev
      );
      fetchChats();
    });

    socketRef.current.on("message:updated", (conversation) => {
      setSelectedChat((prev) =>
        prev?._id === conversation._id ? conversation : prev
      );
      fetchChats();
    });

    fetchChats();
    const interval = setInterval(fetchChats, 10000);

    return () => {
      clearInterval(interval);
      socketRef.current.disconnect();
    };
  }, [uid]);

  const handleSelectChat = (chat) => {
    setSelectedChat(chat);
  };

  const sendMessage = async () => {
    if (!selectedChat || !message.trim()) return;

    const lastQuery = selectedChat.userquery[selectedChat.userquery.length - 1];

    // Optimistic UI update
    const updatedChat = {
      ...selectedChat,
      userquery: [
        ...selectedChat.userquery,
        { query: "", response: message }
      ]
    };
    setSelectedChat(updatedChat);
    setActive((prev) =>
      prev.map((c) => (c._id === updatedChat._id ? updatedChat : c))
    );
    setMessage("");

    try {
      await fetch(url + "/reply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          conversationId: selectedChat._id,
          queryId: lastQuery.query_id,
          agentid: uid,
          response: message,
        }),
      });
      fetchChats(); // sync with backend
    } catch (err) {
      console.error("Error sending message:", err);
    }
  };

  const handleEmojiClick = (emoji) => {
    const input = inputRef.current;
    if (!input) return;

    const start = input.selectionStart;
    const end = input.selectionEnd;
    const newMessage =
      message.substring(0, start) + emoji + message.substring(end);

    setMessage(newMessage);

    setTimeout(() => {
      input.selectionStart = input.selectionEnd = start + emoji.length;
      input.focus();
    }, 0);

    setShowEmojiPicker(false);

  };

  return (
    <WrapperComponent>
  <div className="container-fluid chat-container">
    <div className="row h-100">
      
      {/* Left panel */}
      <div className="col-12 col-md-3 chat-list">
        <div className="chat-list-header d-flex justify-content-between align-items-center p-3">
          <h5 className="mb-0">💬 Chat Assistant</h5>
          <Button size="sm" variant="outline-primary" onClick={fetchChats}>Refresh</Button>
        </div>

        <div className="chat-section">
          <h6 className="section-title">Unassigned</h6>
          <ListGroup variant="flush">
            {unassigned.map((chat) => (
              <ListGroup.Item
                key={chat._id}
                onClick={() => handleSelectChat(chat)}
                className={`chat-item ${selectedChat?._id === chat._id ? "active" : ""}`}
              >
                <div className="d-flex align-items-center">
                  <img
                    src={chat.userPic || "./default-avatar.png"}
                    alt="avatar"
                    className="chat-avatar"
                  />
                  <div className="chat-details ms-2">
                    <strong className="chat-sender">{chat.sender}</strong>
                    <div className="chat-preview text-muted">
                      {chat.userquery[0]?.query || "No messages yet"}
                    </div>
                  </div>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>

        <div className="chat-section mt-3">
          <h6 className="section-title">Active</h6>
          <ListGroup variant="flush">
            {active.map((chat) => (
              <ListGroup.Item
                key={chat._id}
                onClick={() => handleSelectChat(chat)}
                className={`chat-item ${selectedChat?._id === chat._id ? "active" : ""}`}
              >
                <div className="d-flex align-items-center">
                  <img
                    src={chat.userPic || "./default-avatar.png"}
                    alt="avatar"
                    className="chat-avatar"
                  />
                  <div className="chat-details ms-2">
                    <strong className="chat-sender">{chat.sender}</strong>
                    <div className="chat-preview text-muted">
                      {chat.userquery[0]?.query || "No messages yet"}
                    </div>
                  </div>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      </div>

      {/* Middle panel */}
      <div className="col-12 col-md-6 d-flex flex-column chat-window">
        {/* Middle header */}
        <div className="chat-header p-3 border-bottom">
          <h5 className="mb-0">
            {selectedChat ? selectedChat.userName || "Unknown User" : "Conversation"}
          </h5>
          <small className="text-muted">
            {selectedChat ? selectedChat.sender : "Select a chat to start"}
          </small>
        </div>

        <div className="messages-area flex-grow-1 p-3">
          {selectedChat ? (
            selectedChat.userquery.map((q, idx) => (
              <div key={idx} className="message-row">
                {q.query && (
                  <div className="chat-bubble customer">
                    <span>{q.query}</span>
                    <small className="timestamp">User</small>
                  </div>
                )}
                {q.response && (
                  <div className="chat-bubble agent">
                    <span>{q.response}</span>
                    <small className="timestamp">You</small>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="empty-chat text-muted text-center mt-5">
              <p>Select a chat to view messages</p>
            </div>
          )}
        </div>

        <div className="chat-input border-top p-2">
          <InputGroup>
            <Button
                  variant="light"
                  className="input-icon"
                  onClick={() => setShowEmojiPicker((prev) => !prev)}
                >
                  <BsEmojiSmile />
                </Button>

            {/* <Button variant="light" className="input-icon"><BsPaperclip /></Button> */}
            {/* <FormControl
              placeholder="Type a message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && sendMessage()}
            /> */}
            <FormControl
                  ref={inputRef}
                  placeholder="Type a message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                />

            <Button variant="primary" onClick={sendMessage}>Send</Button>
          </InputGroup>
          
              {showEmojiPicker && (
                <div className="emoji-tray">
                  {Object.entries(EMOJI_GROUPS).map(([group, emojis]) => (
                    <div key={group} className="emoji-group">
                      <strong>{group}</strong>
                      <div className="emoji-list">
                        {emojis.map((emoji) => (
                          <span
                            key={emoji}
                            className="emoji-item"
                            onClick={() => handleEmojiClick(emoji)}
                          >
                            {emoji}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

        </div>
      </div>

      {/* Right panel */}
      <div className="col-12 col-md-3 user-info-card p-3">
        {selectedChat ? (
          <Card className="profile-card shadow-sm">
            <div className="profile-header text-center p-3">
              <img
                src={selectedChat.userPic || "./default-avatar.png"}
                alt="User Avatar"
                className="profile-avatar"
              />
              <h5 className="mt-2 mb-0">{selectedChat.userName || "Unknown User"}</h5>
              <small className="text-muted">{selectedChat.userEmail || "N/A"}</small>
            </div>
            <Card.Body>
              <div className="profile-info">
                <p><strong>📱 Phone:</strong> {selectedChat.sender}</p>
                <p><strong>✉️ Email:</strong> {selectedChat.userEmail || "N/A"}</p>
                <p><strong>📝 Notes:</strong> {selectedChat.notes || "No notes yet"}</p>
              </div>
            </Card.Body>
          </Card>
        ) : (
          <div className="text-center text-muted mt-5">
            <p>Select a chat to view user info</p>
          </div>
        )}
      </div>
    </div>
  </div>
</WrapperComponent>
  );
}